import type { ReelItem } from '~/components/PropertyReelsFeed/types';
import type { ShortVideoProperty } from '~/core/entities/property';

import { mapPropertyToShortVideo } from '~/composables/shorts/mappers';
import { getOfflineViews } from '~/composables/usePropertyViewTracking';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { waitUntilFalse } from '~/utils';
import { nuxtPropertyViewsRepository } from '~~/adapters/repositories/nuxt';
import { toRaw } from 'vue';

const HISTORY_PER_PAGE = 10;

function propertyToReelItem(
	property: ShortVideoProperty,
	isFromHistory = false
): ReelItem | null {
	const video = mapPropertyToShortVideo(property);
	if (!video) return null;

	return {
		...video,
		videoType: 'native',
		videoView: property.videoView,
		isFromHistory
	};
}

export function shortVideoToReelItem(video: {
	videoUrl: string | null;
	thumbnailUrl: string | null;
	images?: string[];
	property: ShortVideoProperty;
	videoWidth?: number;
	videoHeight?: number;
}): ReelItem {
	return {
		...video,
		videoType: 'native',
		videoView: video.property.videoView,
		isFromHistory: false,
		property: video.property
	};
}

function getOfflineHistoryAsReelItems(): ReelItem[] {
	return getOfflineViews()
		.filter(view => view.videoData?.property)
		.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
		.map(view => {
			const videoData = view.videoData!;

			return {
				videoUrl: videoData.videoUrl,
				thumbnailUrl: videoData.thumbnailUrl,
				property: videoData.property!,
				videoType: 'native' as const,
				isFromHistory: true,
				videoView: {
					viewId: '',
					maxCompletionRate: view.engagement?.completionRate ?? 0,
					lastCompletionRate: view.engagement?.completionRate ?? 0,
					totalDuration: view.engagement?.duration ?? 0,
					viewCount: 1,
					lastViewedAt: view.timestamp
						? new Date(view.timestamp).toISOString()
						: new Date().toISOString()
				}
			};
		});
}

async function fetchHistoryVideos(
	page: number,
	getCookieData: ReturnType<typeof useCookieData>
): Promise<{ items: ReelItem[]; hasMore: boolean }> {
	const [result, error] = await nuxtPropertyViewsRepository.getVideoHistory(
		{ page, perPage: HISTORY_PER_PAGE },
		getCookieData()
	);

	if (error || !result) {
		return { items: [], hasMore: false };
	}

	const items = (result.videos ?? [])
		.map(p => propertyToReelItem(p, true))
		.filter((item): item is ReelItem => item !== null)
		.reverse();

	const pagination = result.pagination;
	const hasMorePages = (pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

	return { items, hasMore: hasMorePages };
}

export function useVideoHistory() {
	const store = useVideoHistoryStore();
	const { loggedIn } = useUserSession();
	const getCookieData = useCookieData();

	const isLoggedIn = () => import.meta.client && loggedIn.value;

	async function loadHistory(): Promise<void> {
		if (store.isHistoryLoaded) return;

		if (store.isLoadingHistory) {
			await waitUntilFalse(() => store.isLoadingHistory);
			return;
		}

		store.isLoadingHistory = true;
		store.historyPage = 1;

		try {
			if (isLoggedIn()) {
				const result = await fetchHistoryVideos(1, getCookieData);
				store.setItems(result.items);
				store.hasMoreHistory = result.hasMore;
			} else {
				const offlineItems = getOfflineHistoryAsReelItems();
				store.setItems(offlineItems);
				store.hasMoreHistory = false;
			}
			store.isHistoryLoaded = true;
		} finally {
			store.isLoadingHistory = false;
		}
	}

	async function loadMoreHistory(): Promise<number> {
		if (store.isLoadingHistory || !store.hasMoreHistory || !isLoggedIn()) {
			return 0;
		}

		store.isLoadingHistory = true;

		try {
			const nextPage = store.historyPage + 1;
			const result = await fetchHistoryVideos(nextPage, getCookieData);

			if (result.items.length > 0) {
				store.prependItems(result.items);
				store.hasMoreHistory = result.hasMore;
				store.historyPage = nextPage;
				return result.items.length;
			}
			store.hasMoreHistory = false;
			return 0;
		} finally {
			store.isLoadingHistory = false;
		}
	}

	function resetHistory(): void {
		store.reset();
	}

	return { loadHistory, loadMoreHistory, resetHistory };
}
